import { CommonModule } from '@angular/common'
import { A11yModule } from '@angular/cdk/a11y'
import { TextFieldModule } from '@angular/cdk/text-field'
import { GoogleMapsModule } from '@angular/google-maps'
import { Component, Input, type OnInit } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { CptGoogleTagmanagerService, CustomerPortalSharedModule } from '@inside-hub-app/customer-portal-shared'
import { type CustomerPortalConfig } from '@inside-hub-app/customer-portal-config'
import { EfRemoteConfigurationService } from '@inside-hub-app/ef-remote-config'
import { HubChildTranslateModule } from '@inside-hub-app/hub-translate'
import * as moment from 'moment'
import { NgxGlideModule } from 'ngx-glide'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import {
  type CarmatoVehicleDataDTO, type CarmatoValuationDTO, CarmatoService
} from '../../../../services/carmato.service'
import { SharedService } from '../../../../services/shared.service'

export interface VehicleValuation {
  vehicle: string
  vehicleData: CarmatoVehicleDataDTO
  valuations: CarmatoValuationDTO[]
}

export interface Validity {
  days: number
  hours: number
  minutes: number
}

@Component({
  selector: 'customer-portal-app-revolution-marketplace-valuations',
  templateUrl: './marketplace-valuations.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    CustomerPortalSharedModule,
    HubChildTranslateModule,
    NgxSkeletonLoaderModule,
    NgxGlideModule,
    GoogleMapsModule,
    A11yModule,
    TextFieldModule
  ]
})
export class RevolutionMarketplaceValuationsComponent implements OnInit {
  @Input() valuations: CarmatoValuationDTO[] = []
  vehiclesValuations: VehicleValuation[] = []
  displayedColumns: string[] = ['offerFrom', 'bid', 'created']
  currency: string
  currencyLocale: string
  marketPlaceUrl: string
  loading = true

  constructor(
    private readonly carmatoService: CarmatoService,
    private readonly sharedService: SharedService,
    private readonly cptGtmService: CptGoogleTagmanagerService,
    private readonly remoteConfigService: EfRemoteConfigurationService<CustomerPortalConfig>
  ) {
    this.currency = this.remoteConfigService.get('currency')
    this.currencyLocale = this.remoteConfigService.get('currencyLocale')
    this.marketPlaceUrl = this.remoteConfigService.get('marketPlace.url')
  }

  sendContentModulesData(expanded: boolean, contentModuleLabel: string): void {
    this.cptGtmService.sendContentModulesData(
      'Accordion',
      expanded ? 'Open' : 'Close',
      this.sharedService.translateLink(contentModuleLabel) ?? '',
      `Accordion|${this.sharedService.translateLink(contentModuleLabel) ?? ''}`,
      expanded ? 'Open' : 'Close'
    )
  }

  calculateValidity(hours: number): Validity {
    const duration = moment.duration(hours, 'hours')
    return {
      days: duration.days(),
      hours: duration.hours(),
      minutes: duration.minutes()
    }
  }

  // Used to color alternate rows
  oddEven(index: number): string {
    return index % 2 === 0 ? 'even' : 'odd'
  }

  lessThanOneMonthOld(v: CarmatoValuationDTO): boolean {
    const momentBidCreated = moment(v.dateCreated)
    const now = moment(new Date())
    const bidAgeHours = moment.duration(now.diff(momentBidCreated)).asHours()
    return bidAgeHours < 30 * 24
  }

  ngOnInit(): void {
    this.carmatoService.getValuations().subscribe(
      valuations => {
        this.loading = false
        this.valuations = valuations

        if (this.valuations?.[0] != null) {
          this.valuations.forEach(v => {
            const haveVehicleValuation = this.vehiclesValuations.find(
              ({ vehicle }) => vehicle === v.vehicle
            )
            if (this.lessThanOneMonthOld(v)) {
              if (haveVehicleValuation != null) {
                haveVehicleValuation.valuations.push(v)
              } else {
                this.vehiclesValuations.push({
                  vehicle: v.vehicle,
                  vehicleData: v.vehicleData,
                  valuations: [v]
                })
              }
            }
          })
        }
      },
      () => {
        this.loading = false
      }
    )
  }
}
